import React, { useState, useEffect } from 'react';
import { apiFetch } from '../App';
import ParallaxBackground from '../components/ParallaxBackground';
import { Search, MessageCircle, Phone, FileText, MapPin, Clock, ChevronDown, ExternalLink } from 'lucide-react';

const FAQS = [
  { cat: 'Booking', q: 'How do I book a grooming appointment?', a: 'Select your nearest store, choose a service, pick a date and time, and confirm. You can pay online or after the service.' },
  { cat: 'Booking', q: 'Can I reschedule my booking?', a: 'Yes, you can reschedule up to 2 hours before your appointment from your Paws account or by calling the store.' },
  { cat: 'Payments', q: 'What payment methods do you accept?', a: 'We accept UPI, cards, net banking (online), and cash/UPI/card at the store.' },
  { cat: 'Payments', q: 'How do refunds work?', a: 'Refunds are processed within 5-7 business days to your original payment method.' },
  { cat: 'Credits', q: 'How do I earn and use credits?', a: 'You earn 5% credits on every completed booking. Use up to 20% of your order value with credits on your next booking.' },
  { cat: 'Services', q: 'Is grooming safe for puppies?', a: 'Yes! We recommend grooming from 12 weeks old. Our groomers are trained to handle puppies gently.' },
];

export default function SupportPage() {
  const [nodes, setNodes] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedFaq, setExpandedFaq] = useState(null);

  useEffect(() => {
    apiFetch('/api/nodes').then(setNodes).catch(() => {});
  }, []);

  const stores = nodes.filter(n => n.type === 'STORE' && !n.comingSoon);
  const filteredFaqs = FAQS.filter(f => !searchQuery || f.q.toLowerCase().includes(searchQuery.toLowerCase()) || f.a.toLowerCase().includes(searchQuery.toLowerCase()));

  return (
    <div className="relative min-h-screen pt-20" style={{ background: 'var(--bg-primary)' }}>
      <ParallaxBackground />
      
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 relative z-10">
        {/* Hero */}
        <div className="text-center mb-12" data-testid="support-hero">
          <h1 className="font-outfit text-3xl sm:text-4xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>
            How can we <span className="gradient-text">help you</span> today?
          </h1>
          <div className="max-w-md mx-auto relative mt-6">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-secondary)' }} />
            <input
              className="petfolk-input pl-11"
              placeholder="Search FAQs..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              data-testid="support-search"
            />
          </div>
        </div>

        {/* Contact Options */}
        <div className="grid sm:grid-cols-3 gap-6 mb-12" data-testid="contact-options">
          <div className="petfolk-card p-6 text-center">
            <div className="w-14 h-14 rounded-2xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-4">
              <MessageCircle size={24} className="text-green-600" />
            </div>
            <h3 className="font-outfit font-bold mb-2" style={{ color: 'var(--text-primary)' }}>WhatsApp Support</h3>
            <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>Chat with us instantly</p>
            <a href="https://wa.me/919160153336" target="_blank" rel="noreferrer" className="btn-primary text-sm inline-flex" data-testid="whatsapp-btn">
              Start Chat <ExternalLink size={14} />
            </a>
            <p className="text-xs mt-2" style={{ color: 'var(--text-secondary)' }}>9 AM – 9 PM IST, Every Day</p>
          </div>
          <div className="petfolk-card p-6 text-center">
            <div className="w-14 h-14 rounded-2xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mx-auto mb-4">
              <Phone size={24} className="text-blue-600" />
            </div>
            <h3 className="font-outfit font-bold mb-2" style={{ color: 'var(--text-primary)' }}>Call Us</h3>
            <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>Speak with our team</p>
            <a href="tel:+919160153336" className="btn-outline text-sm inline-flex" data-testid="call-btn">
              +91 9160153336
            </a>
            <p className="text-xs mt-2" style={{ color: 'var(--text-secondary)' }}>9 AM – 9 PM IST, Every Day</p>
          </div>
          <div className="petfolk-card p-6 text-center">
            <div className="w-14 h-14 rounded-2xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center mx-auto mb-4">
              <FileText size={24} className="text-purple-600" />
            </div>
            <h3 className="font-outfit font-bold mb-2" style={{ color: 'var(--text-primary)' }}>Email Support</h3>
            <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>For detailed inquiries</p>
            <a href="mailto:support@petfolk.app" className="btn-secondary text-sm inline-flex" data-testid="email-btn">
              support@petfolk.app
            </a>
          </div>
        </div>

        {/* Store Locations */}
        <div className="mb-12" data-testid="store-locations">
          <h2 className="font-outfit text-2xl font-bold text-center mb-6" style={{ color: 'var(--text-primary)' }}>Our Locations</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {stores.map(n => (
              <div key={n.id} className="petfolk-card p-5 flex gap-4" data-testid={`support-store-${n.slug}`}>
                <img src={n.heroImageUrl} alt={n.displayName} className="w-20 h-20 rounded-xl object-cover shrink-0" />
                <div className="flex-1">
                  <h3 className="font-outfit font-bold" style={{ color: 'var(--text-primary)' }}>{n.displayName}</h3>
                  <p className="text-xs flex items-center gap-1 mb-1" style={{ color: 'var(--text-secondary)' }}><MapPin size={10} /> {n.address}</p>
                  <p className="text-xs flex items-center gap-1 mb-2" style={{ color: 'var(--text-secondary)' }}><Clock size={10} /> {n.operatingHours?.open} - {n.operatingHours?.close}</p>
                  <div className="flex gap-2">
                    <a href={`tel:${n.phone}`} className="text-xs font-semibold text-petfolk-orange">Call</a>
                    <a href={n.mapUrl || `https://maps.google.com/?q=${n.lat},${n.lng}`} target="_blank" rel="noreferrer" className="text-xs font-semibold text-petfolk-teal">Directions</a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* FAQs */}
        <div data-testid="faqs-section">
          <h2 className="font-outfit text-2xl font-bold text-center mb-6" style={{ color: 'var(--text-primary)' }}>Frequently Asked Questions</h2>
          <div className="space-y-3 max-w-3xl mx-auto">
            {filteredFaqs.map((faq, i) => (
              <div key={i} className="petfolk-card overflow-hidden" data-testid={`faq-${i}`}>
                <button
                  onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
                  className="w-full p-5 flex items-center justify-between text-left"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: 'var(--border-color)', color: 'var(--text-secondary)' }}>{faq.cat}</span>
                    <span className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>{faq.q}</span>
                  </div>
                  <ChevronDown size={16} className={`transition-transform ${expandedFaq === i ? 'rotate-180' : ''}`} style={{ color: 'var(--text-secondary)' }} />
                </button>
                {expandedFaq === i && (
                  <div className="px-5 pb-5 text-sm animate-fade-in" style={{ color: 'var(--text-secondary)' }}>
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
